import { DatabaseTableSkeletonRefresh } from '@databases/frontend/DatabaseTableSkeletonRefresh';
import React, {
  createContext,
  useContext,
  useSyncExternalStore,
  type ComponentType
} from 'react';

export interface TableV2RenderProps {
  readonly scrollElement: HTMLElement | null;
}

export type TableV2RenderSnapshot =
  | { readonly status: 'unavailable' }
  | { readonly status: 'ready'; readonly Component: ComponentType<TableV2RenderProps> };

/**
 * Read-only React boundary. Identity/lifetime belongs to the application;
 * only the mounted V2 host observes replacements, never existing layouts or cells.
 */
export interface TableV2HostDependencies {
  /**
   * Returns the same binding object until the V2 integration is replaced.
   */
  getSnapshot(): TableV2RenderSnapshot;
  /**
   * Notifies after installing a replacement binding; returns listener cleanup.
   */
  subscribe(onChange: VoidFunction): VoidFunction;
}

const TableV2HostContext = createContext<TableV2HostDependencies | undefined>(undefined);
export const TableV2HostProvider = TableV2HostContext.Provider;

function useTableV2Rendering(): TableV2RenderSnapshot {
  const host = useContext(TableV2HostContext);

  if (!host) {
    throw new Error('Table V2 requires TableV2HostProvider');
  }

  return useSyncExternalStore(host.subscribe, host.getSnapshot, host.getSnapshot);
}

/**
 * Mounted only by the V2 renderer branch. Existing layouts need neither a provider
 * nor a readable V2 binding, including during rollback and database loading.
 */
export function TableV2Host(props: TableV2RenderProps) {
  const rendering = useTableV2Rendering();

  return rendering.status === 'ready' ? (
    <rendering.Component {...props} />
  ) : (
    <DatabaseTableSkeletonRefresh />
  );
}
